/**
 * Body of the alert shown when an import can't start or finish at all
 * (file, template, access, connection): what happened, and how to fix it.
 * For a template mismatch it lists each missing column, points out a column
 * that is only spelled differently, and shows the columns the file does have.
 */
import React from "react";
import { Lightbulb, ArrowRight, ArrowLeft } from "lucide-react";

const ImportErrorExplanation = ({ explanation, isRtl }) => {
  const { message, fix, missingColumns = [], foundColumns = [] } = explanation;
  const Arrow = isRtl ? ArrowLeft : ArrowRight;
  const t = (ar, en) => (isRtl ? ar : en);
  const chip = {
    display: "inline-block", padding: "1px 8px", borderRadius: 6, fontSize: "0.78rem",
    fontWeight: 600, background: "rgba(100,116,139,0.12)", unicodeBidi: "isolate",
  };

  return (
    <div style={{ direction: isRtl ? "rtl" : "ltr", textAlign: "start", display: "flex", flexDirection: "column", gap: 12, fontFamily: "inherit" }}>
      {message && <div style={{ lineHeight: 1.7 }}>{message}</div>}

      {missingColumns.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {missingColumns.map((m) => (
            <div key={m.column} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: "0.85rem" }}>
              {m.found ? (
                <>
                  <span>{t("عمود مكتوب", "Column written as")}</span>
                  <span style={{ ...chip, color: "#ef4444", textDecoration: "line-through" }}>{m.found}</span>
                  <Arrow size={14} />
                  <span>{t("غيّر اسمه إلى", "rename it to")}</span>
                  <span style={{ ...chip, color: "#10b981" }}>{m.rename || m.column}</span>
                </>
              ) : (
                <>
                  <span>{t("عمود ناقص:", "Missing column:")}</span>
                  <span style={{ ...chip, color: "#ef4444" }}>{m.column}</span>
                </>
              )}
            </div>
          ))}
          {foundColumns.length > 0 && (
            <div style={{ fontSize: "0.8rem", opacity: 0.8 }}>
              <div style={{ marginBottom: 6 }}>{t("الأعمدة الموجودة في ملفك:", "Columns found in your file:")}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {foundColumns.map((col) => <span key={col} style={chip}>{col}</span>)}
              </div>
            </div>
          )}
        </div>
      )}

      {fix && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 12px", borderRadius: 10, background: "rgba(16,185,129,0.08)", lineHeight: 1.6 }}>
          <Lightbulb size={16} color="#10b981" style={{ flexShrink: 0, marginTop: 3 }} />
          <span>{fix}</span>
        </div>
      )}
    </div>
  );
};

export default ImportErrorExplanation;
